import { useMemo } from 'react'
import { useTimezoneManager } from '@sportninja/common/hooks/useTimezoneManager'
import { t } from '@sportninja/common/i18n'
import { getCurrentPeriod, getGameStatusLabel } from '../../GameListRowTime'
import getGameState from '../utils/getGameState'
import getUserRole from '../utils/getUserRole'
import { Game, GameState, GameType, GameStatusType } from '../types'

const getGameStatus = (
  game: Game,
  currentPeriod: any,
  isSoccer = false
): string => {
  if (!game || !currentPeriod) {
    return ''
  }
  if (game.shootout) {
    return 'Shootout'
  }
  if (currentPeriod.isOvertime) {
    return isSoccer ? 'Extra Time' : 'Overtime'
  }
  return t('util:capitalize', { text: t('common:final', {}) }) as string
}

interface UseGameCardStateProps {
  game: Game
  gameTypes: GameType[]
  gameStatusTypes: GameStatusType[]
  isSoccer: boolean
  isHockey: boolean
}

interface UseGameCardStateReturn {
  gameState: GameState
  userRole: string | null
  currentPeriod: any
  timezoneData: ReturnType<typeof useTimezoneManager>
  fullGameType: GameType | undefined
  statusText: string
  showHeader: boolean | undefined
}

const useGameCardState = ({
  game,
  gameTypes,
  gameStatusTypes,
  isSoccer,
  isHockey,
}: UseGameCardStateProps): UseGameCardStateReturn => {
  const gameState = useMemo(() => getGameState(game), [game])
  const userRole = useMemo(() => getUserRole(game), [game])

  const currentPeriod = useMemo(
    () => getCurrentPeriod(game, isSoccer, isHockey),
    [game, isSoccer, isHockey]
  )

  const timezoneData = useTimezoneManager({
    date: game.starts_at,
    timezone: game?.venue?.timezone,
  })

  const fullGameType = useMemo(
    () =>
      gameTypes?.find(
        (gt) => parseInt(String(gt?.id), 10) === game?.game_type_id
      ),
    [gameTypes, game?.game_type_id]
  )

  const statusText = useMemo(() => {
    if (!gameState.gameOver) {
      return ''
    }

    const gameStatus = getGameStatus(game, currentPeriod, isSoccer)
    const gameStatusLabel = getGameStatusLabel(
      game.game_status_id,
      gameStatusTypes
    )

    let text =
      gameStatus ||
      (t('util:capitalize', { text: t('common:final', {}) }) as string)

    if (
      game.game_status_id > 1 &&
      gameStatusLabel &&
      gameStatusLabel.length > 0
    ) {
      text += ` - ${gameStatusLabel}`
    }

    if (gameState.statsRolledback) {
      text += ' - [In Review]'
    }

    return text
  }, [game, gameState, currentPeriod, isSoccer, gameStatusTypes])

  // Show header when user has a role, game is live, or upcoming livestream
  const showHeader =
    !!userRole || gameState.isLive || gameState.isUpcomingLivestream

  return {
    gameState,
    userRole,
    currentPeriod,
    timezoneData,
    fullGameType,
    statusText,
    showHeader,
  }
}

export default useGameCardState
